import { useCallback, useRef, useState } from "react";
import { Typography } from "../components/atoms/Typography";
import { GameRuleGeneratedDocumentsTable } from "../components/organisms/GameRuleGeneratedDocumentsTable";
import {
  type GameRuleGeneratedDocumentStatusFilter,
  useGameRuleGeneratedDocuments,
} from "../hooks/useGameRuleGeneratedDocuments";
import { downloadGeneratedGameRuleDocument } from "../services/gameRuleGeneratedDocumentService";
import type { GameRuleGeneratedDocumentRow } from "../types/gameRuleGeneratedDocument";

const STATUS_FILTERS: readonly {
  readonly id: GameRuleGeneratedDocumentStatusFilter;
  readonly label: string;
}[] = [
  { id: "all", label: "All" },
  { id: "review", label: "Pending Review" },
  { id: "approved", label: "Approved" },
  { id: "draft", label: "Draft" },
];

export const PrizeLogicPage = () => {
  const downloadLockRef = useRef(false);
  const [downloadingRowId, setDownloadingRowId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const {
    rows,
    loading,
    error,
    page,
    pageSize,
    totalRows,
    searchInput,
    statusFilter,
    setSearchInput,
    setStatusFilter,
    setPage,
    setPageSize,
  } = useGameRuleGeneratedDocuments();

  const handleDownload = useCallback(async (row: GameRuleGeneratedDocumentRow) => {
    if (downloadLockRef.current) {
      return;
    }
    downloadLockRef.current = true;
    setDownloadError(null);
    setDownloadingRowId(row.id);
    try {
      await downloadGeneratedGameRuleDocument(row);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : "Download failed.");
    } finally {
      downloadLockRef.current = false;
      setDownloadingRowId(null);
    }
  }, []);

  return (
    <section className="page-section flex min-h-0 flex-1 flex-col overflow-hidden">
      <header className="page-header shrink-0">
        <div>
          <Typography as="h1" variant="h1">
            PrizeLogic
          </Typography>
          <p className="page-subtitle">
            Game rules you generated from the Word add-in appear here. Download your documents.
          </p>
        </div>
      </header>

      <div className="page-toolbar mt-6 shrink-0">
        <div className="relative max-w-xl flex-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="search"
            placeholder="Search document name, reviewer, file name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      </div>

      <div className="mt-3 flex shrink-0 flex-wrap gap-2">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.id}
            type="button"
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
              statusFilter === filter.id
                ? "border-primary-500 bg-primary-500 text-white"
                : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
            }`}
            onClick={() => setStatusFilter(filter.id)}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mt-4 shrink-0 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {downloadError && (
        <div className="mt-4 shrink-0 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {downloadError}
        </div>
      )}

      <div className="mt-4 min-h-0 flex-1">
        <GameRuleGeneratedDocumentsTable
          rows={rows}
          totalRows={totalRows}
          isLoading={loading}
          page={page}
          perPage={pageSize}
          onChangePage={setPage}
          onChangeRowsPerPage={setPageSize}
          onDownload={handleDownload}
          downloadingRowId={downloadingRowId}
        />
      </div>
    </section>
  );
};
